// CrownData.ts

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string;
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[];
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const crown: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    en: {
        "url": "",
        "meta": {
            "title": "Dental Crowns Dubai",
            "description": "Looking for dental crowns service in Dubai? We offer dental crowns in Dubai to match the color of your natural teeth &amp; restore the gap created by a missing tooth.",
            "robots": "",
            "canonical": "",
            "og": {}
        },
        "breadcrumbs": ["Home", "Services", "Dental Crowns Dubai"],
        "hero": {
            "title": "Superior Quality Dental Crowns from Elite Body Home",
            "subtitle": "A Smart Solution for Your Beautiful Smile",
            "description": "Whether it's a damaged tooth, a decayed tooth, or a weakened tooth, our crowns are custom-made to match your natural teeth perfectly while providing strong protection against further issues and a stunning, confident smile for years to come. With the latest technology and techniques, Elite Body Home will ensure natural-looking and extremely durable dental crowns for you, elevating your dental treatment experience to the next level."
        },
        "howItWorks": {
            "title": "Steps For Dental Crown Procedure",
            "description": "Getting a dental crown usually takes two visits, with a few steps involved. The objective is to condition the impacted tooth and place a crown that visually and functionally resembles your other natural teeth."
        },
        "procedureSteps": {
            "heading": "",
            "para": "",
            "steps": [
                {
                    "title": "Consultation",
                    "desc": "Your dentist will examine the affected tooth with X-rays or other diagnostic tools to decide whether a crown is the best treatment option.",
                    "icon": "fa-solid fa-user-doctor"
                },
                {
                    "title": "Tooth Preparation",
                    "desc": "The tooth is formed to receive the crown. This could include a removal of some of the outer wall of the tooth.",
                    "icon": "fa-solid fa-tooth"
                },
                {
                    "title": "Impressions",
                    "desc": "Your dentist makes impressions of the prepared tooth and sends them to a dental lab to create a custom crown.",
                    "icon": "fa-solid fa-print"
                },
                {
                    "title": "Temporary Crown",
                    "desc": "A temporary crown is placed to cover up the hole while waiting for your permanent crown to be made.",
                    "icon": "fa-solid fa-clock"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits",
            "subheading": "",
            "para": "Like many dental procedures dental crowns provide a variety of benefits and are among the most frequently recommended restorative treatments in dentistry.",
            "items": [
                {
                    "title": "Protection",
                    "desc": "They shield weak or damaged teeth from further decay, fractures, or breakage.",
                    "icon": "fa-solid fa-shield-alt"
                },
                {
                    "title": "Durability",
                    "desc": "Crowns are constructed from durable materials such as porcelain, ceramic, or metal, making them long-lasting solution.",
                    "icon": "fa-solid fa-gem"
                },
                {
                    "title": "Aesthetic Improvement",
                    "desc": "Crowns can be designed to mimic the coloration and shape of your natural teeth, making your smile look its best.",
                    "icon": "fa-solid fa-face-smile"
                },
                {
                    "title": "Restored Function",
                    "desc": "Crowns return the ability to chew and speak comfortably if tooth damage affected those functions.",
                    "icon": "fa-solid fa-utensils"
                }
            ]
        },
        "eligibility": {
            "heading": "Eligibility",
            "subheading": "",
            "para": "Not all dental conditions call for a crown, but they are perfect when a tooth is significantly compromised or weakened. People that qualify for dental crowns usually have:",
            "items": [
                {
                    "title": "Very Decayed or Damaged Teeth",
                    "desc": "Crowns are typically recommended when fillings or other restorative options are insufficient.",
                    "icon": "fa-solid fa-tooth"
                },
                {
                    "title": "Root Canal-Treated Teeth",
                    "desc": "After this procedure, the tooth can lose strength and become brittle, which is why a crown is needed for protection.",
                    "icon": "fa-solid fa-teeth"
                },
                {
                    "title": "Cracked or Broken Teeth",
                    "desc": "Crowns protect the tooth from more damage by covering the entire tooth structure.",
                    "icon": "fa-solid fa-bone-break"
                },
                {
                    "title": "Worn-Down Teeth",
                    "desc": "But crowns can protect teeth that have become worn down from grinding or erosion from acid.",
                    "icon": "fa-solid fa-teeth-open"
                }
            ]
        },
        "preparationTips": {
            "heading": "Preparation",
            "subheading": "",
            "para": "There are several steps with dental crowning therapy, before receiving a crowning therapy. Having the right approach ahead of time will help not just to make the procedure work but also to promote long-lasting results.",
            "items": [
                {
                    "title": "Consultation with your dentist",
                    "desc": "Talk about your symptoms, goals and medical history.",
                    "icon": "fa-solid fa-comment-medical"
                },
                {
                    "title": "X-Rays & Imaging",
                    "desc": "Your dentist will probably take X-rays to evaluate the tooth and surrounding bone structure.",
                    "icon": "fa-solid fa-x-ray"
                },
                {
                    "title": "Dental Hygiene",
                    "desc": "Practice good oral hygiene leading up to the procedure to help prevent your risk of infection or complications.",
                    "icon": "fa-solid fa-broom"
                },
                {
                    "title": "Understanding the procedure",
                    "desc": "Your dentist will go over the procedure for you, as well as what you can expect, the timeline, and aftercare instructions.",
                    "icon": "fa-solid fa-book-open-reader"
                },
                {
                    "title": "Temporary Crown Care",
                    "desc": "If they put in a temporary crown, then avoid sticky foods and brush soft to protect the temporary — it will come out if you eat the wrong things.",
                    "icon": "fa-solid fa-crown"
                }
            ]
        },
        "results": {
            "heading": "Results",
            "subheading": "",
            "para": "However, dental crowns give instant functional and aesthetic advantages. Once the crown is cemented in place, you'll find that this is an improvement over the previous tooth in terms of strength, aesthetics, and function.",
            "items": [
                {
                    "title": "Improved Chewing & Speaking",
                    "desc": "They restore the normal function of the tooth so that you can chew and speak without pain.",
                    "icon": "fa-solid fa-comments"
                },
                {
                    "title": "Enhanced Smile",
                    "desc": "Crowns enhance damaged or stained teeth, resulting in a more beautiful smile.",
                    "icon": "fa-solid fa-face-grin"
                },
                {
                    "title": "Long-term Protection",
                    "desc": "With proper care, veneers have an average lifespan of about 15 years, during which they continue to look and feel very strong and natural.",
                    "icon": "fa-solid fa-shield-halved"
                },
                {
                    "title": "Increased Confidence",
                    "desc": "Dental crowns increase your confidence in everyday activities as well as your smile by restoring the strength, appearance, and function of your teeth.",
                    "icon": "fa-solid fa-face-grin-stars"
                }
            ]
        },
        "aftercareTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    question: "How long do dental crowns last?",
                    answer: "Dental crowns typically last between 10 to 15 years, but with proper care, some crowns can last even longer.",
                },
                {
                    question: "Do crowns look like natural teeth?",
                    answer: "Yes, dental crowns are custom-made to match the size, shape, and color of your surrounding teeth, providing a natural appearance.",
                },
                {
                    question: "What materials are used for crowns?",
                    answer: "Crowns can be made from a variety of materials, including porcelain, ceramic, metal alloys, or a combination. Porcelain and ceramic are often chosen for front teeth because they closely mimic natural tooth enamel.",
                },
                {
                    question: "Are dental crowns painful?",
                    answer: "The procedure itself is typically painless due to local anesthesia, though some patients may experience mild discomfort after the procedure, which usually subsides within a few days.",
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    },
    shared: {
        images: {
            main: "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/New-Service-Banjcggjner-Image-8-scaled.jpg",
            beforeAfter: [
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Before-After-Service-Images-8-1.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/sdfsdgs-1.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/dfgdrgd.jpg"
            ],
            introductionImage: ""
        },
        product: {
            id: 1,
            title: "Dental Crowns in Dubai",
            description: "Professional teeth crowns service in Dubai to remove plaque, tartar, and stains. Promotes healthy gums, fresh breath, and a brighter smile with expert dental care.",
            price: 399.0
        },
    },
    ar: {
        "url": "",
        "meta": {
            "title": "",
            "description": "",
            "robots": "",
            "canonical": "",
            "og": {}
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "تركيب تيجان الأسنان في دبي"],
        "hero": {
            "title": "تركيب تيجان الأسنان عالية الجودة من ايليت بودي هوم",
            "subtitle": "حل ذكي لابتسامة جميلة",
            "description": "سواء كنت تعاني من سن متضرر أو متسوس أو ضعيف، نقدم لك تيجانًا مصممة خصيصًا لتتناسب تمامًا مع شكل ولون أسنانك الطبيعية، مع توفير حماية قوية ضد استمرار التلف، وابتسامة واثقة تدوم لسنوات. نستخدم في ايليت بودي هوم أحدث التقنيات والأساليب لنقدم لك تيجان أسنان طبيعية المظهر ومتينة للغاية، لنرتقي بتجربتك العلاجية إلى مستوى جديد من التميز."
        },
        "howItWorks": {
            "title": "خطوات تركيب تيجان الأسنان",
            "description": "عادةً ما يتطلب تركيب تيجان الأسنان زيارتين لطبيب الأسنان، ويشمل الإجراء عدة مراحل تهدف إلى تجهيز السن المصاب وتركيب تاج يشبه الأسنان الطبيعية في الشكل والوظيفة."
        },
        "procedureSteps": {
            "heading": "",
            "para": "",
            "steps": [
                {
                    "title": "استشارة الطبيب",
                    "desc": "يقوم الطبيب بفحص السن المتأثر باستخدام الأشعة السينية أو أدوات تشخيصية أخرى لتحديد ما إذا كان التاج هو الخيار العلاجي الأنسب.",
                    "icon": "fa-solid fa-user-doctor"
                },
                {
                    "title": "تحضير السن",
                    "desc": "يتم تشكيل السن لاستقبال التاج، وقد يتضمن ذلك إزالة جزء من الطبقة الخارجية للسن.",
                    "icon": "fa-solid fa-tooth"
                },
                {
                    "title": "أخذ الطباعات",
                    "desc": "يقوم الطبيب بأخذ طبعة دقيقة للسن المحضر، ثم تُرسل إلى مختبر الأسنان لصناعة تاج مخصص.",
                    "icon": "fa-solid fa-print"
                },
                {
                    "title": "التاج المؤقت",
                    "desc": "يُركب تاج مؤقت لحماية السن خلال فترة الانتظار إلى حين الانتهاء من صنع التاج الدائم.",
                    "icon": "fa-solid fa-clock"
                }
            ]
        },
        "benefits": {
            "heading": "الفوائد",
            "subheading": "",
            "para": "توفر تيجان الأسنان مجموعة من الفوائد مثل العديد من إجراءات طب الأسنان، وتُعد من أكثر طرق العلاج الترميمي شيوعًا في طب الأسنان.",
            "items": [
                {
                    "title": "الحماية",
                    "desc": "توفر حماية فعالة للأسنان الضعيفة أو المتضررة، وتمنع تزايد التسوس أو التشقق أو الكسر.",
                    "icon": "fa-solid fa-shield-alt"
                },
                {
                    "title": "التحمل والصلابة",
                    "desc": "تصنع التيجان من مواد متينة مثل البورسلين أو السيراميك أو المعدن أو الزيركون، مما يجعلها حلاً طويل الأمد.",
                    "icon": "fa-solid fa-gem"
                },
                {
                    "title": "تحسين المظهر الجمالي",
                    "desc": "تُصمم التيجان لتشبه لون وشكل الأسنان الطبيعية، مما يمنحك ابتسامة متناسقة وجذابة.",
                    "icon": "fa-solid fa-face-smile"
                },
                {
                    "title": "استعادة الوظيفة",
                    "desc": "تساعد في استعادة القدرة على المضغ والتحدث براحة إذا كانت الأضرار السنية قد أثرت على تلك الوظائف.",
                    "icon": "fa-solid fa-utensils"
                }
            ]
        },
        "eligibility": {
            "heading": "الأهلية",
            "subheading": "",
            "para": "لا تُستخدم تيجان الأسنان لجميع الحالات، لكنها تعتبر خيارًا مثاليًا عندما يكون السن متضررًا أو ضعيفًا بشكل كبير. الأشخاص المؤهلون لتركيب تيجان الأسنان هم الأشخاص الذين يعانون من:",
            "items": [
                {
                    "title": "الأسنان المصابة بالتسوس الشديد أو المتآكلة",
                    "desc": "يُوصى باستخدام التاج لتقوية السن، عندما لا تكون الحشوات أو أي علاج ترميمي آخر كافية.",
                    "icon": "fa-solid fa-tooth"
                },
                {
                    "title": "الأسنان التي خضعت لعلاج قناة الجذر",
                    "desc": "قد يفقد السن جزءًا كبيرًا من قوته ويصبح أكثر هشاشة بعد علاج عصب الأسنان، لذا يُستخدم التاج لحمايته من الانكسار.",
                    "icon": "fa-solid fa-teeth"
                },
                {
                    "title": "الأسنان المتشققة أو المكسورة",
                    "desc": "يُغطّي التاج كامل بنية السن، مما يمنع تفاقم الكسر أو تعرض السن لأضرار إضافية.",
                    "icon": "fa-solid fa-bone-break"
                },
                {
                    "title": "الأسنان المتآكلة",
                    "desc": "يمكن للتيجان أن تحمي الأسنان التي تآكلت نتيجة صرير الأسنان أو التآكل الحمضي.",
                    "icon": "fa-solid fa-teeth-open"
                }
            ]
        },
        "preparationTips": {
            "heading": "الاستعداد",
            "subheading": "",
            "para": "يجب اتباع عدة خطوات قبل الخضوع لعلاج تركيب تاج الأسنان، حيث يساعد الاستعداد الجيد في نجاح الإجراء وضمان نتائج تدوم طويلًا.",
            "items": [
                {
                    "title": "استشارة طبيبك",
                    "desc": "ابدأ بمناقشة الأعراض، وأهدافك، وتاريخك الطبي مع طبيب الأسنان.",
                    "icon": "fa-solid fa-comment-medical"
                },
                {
                    "title": "الأشعة السينية والتصوير",
                    "desc": "سيقوم الطبيب عادةً بالتقاط صورة أشعة سينية، لتقييم حالة السن والعظم المحيط به بدقة.",
                    "icon": "fa-solid fa-x-ray"
                },
                {
                    "title": "العناية بنظافة الفم",
                    "desc": "حافظ على نظافة فمك جيدًا قبل الإجراء، لتقليل احتمالية الإصابة بالعدوى أو أي مضاعفات.",
                    "icon": "fa-solid fa-broom"
                },
                {
                    "title": "فهم خطوات الإجراء",
                    "desc": "سيشرح لك الطبيب مراحل العلاج، والمدة الزمنية، والتعليمات الخاصة بالعناية بعد التركيب.",
                    "icon": "fa-solid fa-book-open-reader"
                },
                {
                    "title": "العناية بالتاج المؤقت",
                    "desc": "إذا تم وضع تاج مؤقت، تجنب الأطعمة اللزجة أو الصلبة، واستخدم فرشاة ناعمة، لأن التاج المؤقت قد ينفصل بسهولة إذا لم تتم حمايته.",
                    "icon": "fa-solid fa-crown"
                }
            ]
        },
        "results": {
            "heading": "النتائج",
            "subheading": "",
            "para": "توفر تيجان الأسنان فوائد فورية من حيث الوظيفة والمظهر الجمالي. فبمجرد تثبيت التاج في مكانه، ستلاحظ تحسنًا واضحًا للسن المتضرر، من حيث القوة والشكل والوظيفة.",
            "items": [
                {
                    "title": "تحسن المضغ والنطق",
                    "desc": "تُعيد التيجان الوظيفة الطبيعية للسن، مما يتيح لك المضغ والتحدث دون ألم.",
                    "icon": "fa-solid fa-comments"
                },
                {
                    "title": "ابتسامة أكثر جاذبية",
                    "desc": "يُحسن العلاج مظهر الأسنان التالفة أو المتصبغة، مما يمنحك ابتسامة أكثر جمالًا وتناسقًا.",
                    "icon": "fa-solid fa-face-grin"
                },
                {
                    "title": "حماية طويلة الأمد",
                    "desc": "مع العناية المناسبة، تدوم التيجان في المتوسط 15 عامًا تقريبًا، مع الحفاظ على مظهرها الطبيعي وقوتها.",
                    "icon": "fa-solid fa-shield-halved"
                },
                {
                    "title": "زيادة الثقة بالنفس",
                    "desc": "تُعزز تيجان الأسنان ثقتك بنفسك سواء أثناء التحدث أو الابتسام، من خلال استعادة قوة الأسنان ومظهرها ووظيفتها.",
                    "icon": "fa-solid fa-face-grin-stars"
                }
            ]
        },
        "aftercareTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "faqData": {
            "heading": "",
            "subheading": "",
            "items": []
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    },

};
